import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { HeartPulse, ShieldCheck } from "lucide-react";

/**
 * Iter60.p64 — Admin → Sick Pay Config page.
 *
 * Controls every tier boundary + percentage + probation defaults +
 * short-illness rule + medical-certificate requirement. Admin-only
 * write (Officer/HR/Manager 403); all authenticated roles can read.
 */
export default function SickPayConfig() {
    const { user } = useAuth();
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/sick-pay-config");
                setConfig(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (!isAdmin) {
        return (
            <AppShell title="Sick Pay Config">
                <div className="asl-card p-6" data-testid="sick-pay-forbidden">
                    <p className="text-sm">This page is restricted to System Admins.</p>
                </div>
            </AppShell>
        );
    }

    const setNum = (key) => (e) =>
        setConfig({ ...config, [key]: Number(e.target.value) });

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...config };
            delete payload.updated_at;
            delete payload.updated_by;
            const { data } = await api.patch("/admin/sick-pay-config", payload);
            setConfig(data);
            toast.success("Sick pay config saved.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !config) {
        return (
            <AppShell title="Sick Pay Config">
                <div className="asl-card p-6" data-testid="sick-pay-loading">Loading…</div>
            </AppShell>
        );
    }

    return (
        <AppShell title="Sick Pay Config">
            <section className="space-y-5" data-testid="sick-pay-page">
                <header className="flex items-center gap-3">
                    <HeartPulse className="h-6 w-6 asl-crimson-text" />
                    <div>
                        <h2 className="text-xl font-bold asl-navy-text">
                            Sick Pay Configuration
                        </h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            Tiered sick pay per Somali Labor Law Article 70. Tiers apply
                            based on cumulative sick days used in the current fiscal cycle.
                        </p>
                    </div>
                </header>

                <div className="asl-card p-5 space-y-4" data-testid="sick-pay-form">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-3 border-b border-[color:var(--asl-border,#E5E7EB)]">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Default probation (months)</Label>
                            <Input
                                type="number"
                                min={0}
                                max={36}
                                value={config.default_probation_months}
                                onChange={setNum("default_probation_months")}
                                data-testid="sick-cfg-probation-months"
                            />
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                Used when an employee has no explicit `probation_end_date`.
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Short illness threshold (days)</Label>
                            <Input
                                type="number"
                                min={0}
                                max={7}
                                value={config.short_illness_days}
                                onChange={setNum("short_illness_days")}
                                data-testid="sick-cfg-short-illness-days"
                            />
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                1 through this many days = "short illness"; paid/unpaid controlled below.
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase">Toggles</Label>
                            <div className="flex items-center gap-2 mt-2">
                                <Switch
                                    checked={!!config.short_illness_paid}
                                    onCheckedChange={(v) =>
                                        setConfig({ ...config, short_illness_paid: v })}
                                    data-testid="sick-cfg-short-paid-toggle"
                                />
                                <span className="text-xs">Short illness paid at 100%</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={!!config.require_medical_certificate}
                                    onCheckedChange={(v) =>
                                        setConfig({ ...config, require_medical_certificate: v })}
                                    data-testid="sick-cfg-require-cert-toggle"
                                />
                                <span className="text-xs">Require medical certificate</span>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h3 className="text-sm font-bold asl-navy-text">Tiers (cumulative sick days in cycle)</h3>
                        {[
                            { n: 1, min: "tier_1_days_min", max: "tier_1_days_max", pct: "tier_1_percentage" },
                            { n: 2, min: "tier_2_days_min", max: "tier_2_days_max", pct: "tier_2_percentage" },
                            { n: 3, min: "tier_3_days_min", max: "tier_3_days_max", pct: "tier_3_percentage" },
                        ].map((t) => (
                            <div
                                key={t.n}
                                className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
                                data-testid={`sick-cfg-tier-${t.n}-row`}
                            >
                                <div>
                                    <Label className="text-xs uppercase">Tier {t.n}</Label>
                                    <div className="text-sm font-semibold asl-navy-text">
                                        Tier {t.n}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase">Days from</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={config[t.min]}
                                        onChange={setNum(t.min)}
                                        data-testid={`sick-cfg-tier-${t.n}-min`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase">Days to</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={config[t.max]}
                                        onChange={setNum(t.max)}
                                        data-testid={`sick-cfg-tier-${t.n}-max`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase">Percentage</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={config[t.pct]}
                                        onChange={setNum(t.pct)}
                                        data-testid={`sick-cfg-tier-${t.n}-pct`}
                                    />
                                </div>
                            </div>
                        ))}
                        <div
                            className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end pt-3 border-t border-[color:var(--asl-border,#E5E7EB)]"
                            data-testid="sick-cfg-tier-4-row"
                        >
                            <div>
                                <Label className="text-xs uppercase">Tier 4</Label>
                                <div className="text-sm font-semibold asl-crimson-text">
                                    Overflow tier
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase">Days from</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={config.tier_4_days_min}
                                    onChange={setNum("tier_4_days_min")}
                                    data-testid="sick-cfg-tier-4-min"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase">— unlimited</Label>
                                <Input type="text" disabled value="∞" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase">Percentage</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={config.tier_4_percentage}
                                    onChange={setNum("tier_4_percentage")}
                                    data-testid="sick-cfg-tier-4-pct"
                                />
                                <p className="text-xs text-[color:var(--asl-muted)]">
                                    0 = unpaid; contract-defined overrides may layer on top later.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 border-t border-[color:var(--asl-border,#E5E7EB)] pt-4">
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="asl-navy-bg text-white hover:opacity-90"
                            data-testid="sick-cfg-save-btn"
                        >
                            <ShieldCheck className="h-4 w-4 mr-1" />
                            {saving ? "Saving…" : "Save config"}
                        </Button>
                    </div>
                </div>
            </section>
        </AppShell>
    );
}
